// apps/client/src/pages/reports/SharedMarketingSourceReportPage.tsx
//
// Public, unauthenticated read-only view of the marketing monthly report.
// Mirrors the seven sibling shared pages (SharedSuccessionReadinessPage et al).
//
// TWO THINGS THIS PAGE MUST KEEP DOING
// ------------------------------------
// 1. RENDER THE COMMENTARY AS PLAIN TEXT. `panels` is tenant-authored free text
//    served to an audience with no login, from the app origin. React escapes by
//    default, which is the whole defence. Never introduce dangerouslySetInnerHTML
//    or a markdown renderer that emits raw HTML here.
// 2. RENDER THE SCOPE NOTE. The recipient cannot ask a clarifying question, and
//    without it a month's figure reads as the group's entire lead flow rather
//    than the leads Valutten captured. It comes from the payload rather than
//    being hardcoded so it stays true if the query changes.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Megaphone, Calendar, Info, AlertTriangle } from 'lucide-react';
import { functions } from '../../lib/firebase';

interface SourceCount {
  source: string;
  leads: number;
}
interface CampaignCount {
  source: string;
  campaign: string;
  leads: number;
}
interface EnquirySourceCount {
  category: string;
  deals: number;
  isUnknown: boolean;
}
interface CrmEnquirySourceMonth {
  totalDeals: number;
  knownDeals: number;
  unknownDeals: number;
  // Null when there were no deals. The recipient cannot ask what 0% means.
  coveragePct: number | null;
  byCategory: EnquirySourceCount[];
}
// Spend fields are OPTIONAL here on purpose: this page renders a STORED
// snapshot, and links minted before ad-spend shipped carry none. Guard every
// read with `?.` / `?? []` so an old link still renders cleanly.
interface ProviderSpend {
  provider: string;
  spendCents: number;
  clicks: number;
  impressions: number;
  capturedLeads: number;
  costPerLeadCents: number | null;
}
interface LeadMonth {
  month: string;
  totalLeads: number;
  withCampaign: number;
  bySource: SourceCount[];
  byCampaign: CampaignCount[];
  spendByProvider?: ProviderSpend[];
  totalSpendCents?: number;
  // Optional because this page renders immutable snapshots created before the
  // MyCRM enquiry-source series shipped.
  crmEnquirySource?: CrmEnquirySourceMonth;
  /** True while the month was still running when the snapshot was taken. */
  monthToDate?: boolean;
  /** False when no ad spend data existed for the month at all. */
  hasSpendRows?: boolean;
}
interface Panels {
  social: string;
  seo: string;
  leadGen: string;
}
interface ShareData {
  shareId: string;
  companyName: string;
  createdAt: string;
  data: {
    report: {
      months: LeadMonth[];
      totals: {
        totalLeads: number;
        withCampaign: number;
        campaignCoveragePct: number | null;
        distinctCampaigns: number;
        distinctSources: number;
        firstCapturedMonth: string | null;
        totalSpendCents?: number;
        overallCostPerLeadCents?: number | null;
        crmDeals?: number;
        crmKnownDeals?: number;
        crmUnknownDeals?: number;
        crmCoveragePct?: number | null;
      };
      provenance: {
        scopeNote: string;
        timezone: string;
        enquirySourceAvailable: boolean;
        enquirySourceNote?: string;
        spendNote?: string;
      };
    };
    panels: Panels | null;
    panelMonth: string | null;
    generatedAt: string;
    /**
     * Whether this snapshot carries ad spend. Stated on the page, so a missing
     * spend section never leaves the recipient guessing whether it means "no
     * spend" or "not shared". Optional: links minted before the option existed
     * carry spend and do not set it.
     */
    spendIncluded?: boolean;
  };
}

const prettySource = (s: string) =>
  s
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

const prettyMonth = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  if (!y || !mo) return m;
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const PANEL_LABELS: Array<{ key: keyof Panels; label: string }> = [
  { key: 'social', label: 'Social media' },
  { key: 'seo', label: 'SEO' },
  { key: 'leadGen', label: 'Lead generation' },
];

/** Integer cents -> AUD. dp=0 for totals, dp=2 for cost-per-lead. */
const aud = (cents: number, dp = 0) =>
  `$${(cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;

const SharedMarketingSourceReportPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('No share token provided');
      setLoading(false);
      return;
    }
    let cancelled = false;

    const validate = async () => {
      try {
        const fn = httpsCallable<{ token: string }, ShareData>(
          functions,
          'validateMarketingReportShareToken',
        );
        const result = await fn({ token });
        if (cancelled) return;
        setShareData(result.data);
      } catch (err) {
        if (cancelled) return;
        // Deliberately generic. An expired, revoked, unknown and
        // wrong-signature token all read the same to an anonymous caller, so
        // the page is not an oracle for probing which tokens ever existed.
        console.error('[SharedMarketingSourceReport] validation failed:', err);
        setError('This link is invalid, has expired, or has been revoked.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <p className="text-slate-400">Loading report…</p>
      </div>
    );
  }

  if (error || !shareData) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" aria-hidden="true" />
          <p className="text-slate-300">{error ?? 'Report unavailable.'}</p>
          <p className="mt-4 text-sm text-slate-500">
            Ask whoever sent it for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  const { report, panels, panelMonth, generatedAt, spendIncluded } = shareData.data;
  const anyMonthToDate = report.months.some((m) => m.monthToDate === true);

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="p-8 max-w-5xl mx-auto">
        <div className="rounded-2xl p-8 mb-8 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-4 -right-4 w-64 h-64 bg-white/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-white/20 rounded-full blur-2xl" />
          </div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <Megaphone className="w-10 h-10 text-white" aria-hidden="true" />
              <h1 className="text-4xl font-bold text-white">Marketing Report</h1>
            </div>
            <p className="text-white/90 text-lg mb-2">{shareData.companyName}</p>
            <div className="flex flex-wrap gap-3 mt-4">
              <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm text-white/90">
                Shared Report - Read Only
              </div>
              <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm text-white/90">
                Powered by VALUTTEN
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-400" aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400">Generated</p>
              <p className="text-slate-200">
                {new Date(generatedAt).toLocaleString('en-AU', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Scope note, before any figure. */}
        <div className="bg-blue-950/30 rounded-xl border border-blue-900/60 p-5 mb-6 flex gap-3">
          <Info className="w-5 h-5 shrink-0 text-blue-400 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-slate-300">
            <p>{report.provenance.scopeNote}</p>
            {report.provenance.enquirySourceAvailable && report.provenance.enquirySourceNote && (
              <p className="mt-2 text-slate-400">{report.provenance.enquirySourceNote}</p>
            )}
            {/* An absent spend section means one of two very different things.
                Say which, rather than letting the reader assume zero. */}
            {spendIncluded === false && (
              <p className="mt-2 text-slate-400">
                Ad spend and cost per lead are not included in this link.
              </p>
            )}
            {anyMonthToDate && (
              <p className="mt-2 text-amber-300/90">
                A month marked &ldquo;month to date&rdquo; was still running when this report was
                taken, so it covers part of a month and is not comparable with the completed
                months beside it.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Leads captured', value: String(report.totals.totalLeads) },
            ...(report.totals.totalSpendCents != null
              ? [{ label: 'Total ad spend', value: aud(report.totals.totalSpendCents) }]
              : []),
            ...(report.totals.overallCostPerLeadCents != null
              ? [{ label: 'Cost / lead', value: aud(report.totals.overallCostPerLeadCents, 2) }]
              : []),
            {
              label: 'With a campaign',
              value:
                report.totals.campaignCoveragePct !== null
                && report.totals.campaignCoveragePct !== undefined
                  ? `${report.totals.campaignCoveragePct}%`
                  : 'n/a',
            },
            { label: 'Campaigns', value: String(report.totals.distinctCampaigns) },
            { label: 'Sources', value: String(report.totals.distinctSources) },
            ...(report.totals.crmDeals != null && report.totals.crmDeals > 0
              ? [
                  { label: 'CRM deals', value: String(report.totals.crmDeals) },
                  {
                    label: 'CRM source coverage',
                    // `?? 0` here printed "0% known" for a snapshot that simply
                    // measured nothing.
                    value:
                      report.totals.crmCoveragePct !== null
                      && report.totals.crmCoveragePct !== undefined
                        ? `${report.totals.crmCoveragePct}%`
                        : 'n/a',
                  },
                ]
              : []),
          ].map((s) => (
            <div
              key={s.label}
              className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5"
            >
              <div className="text-[11px] uppercase tracking-wider text-slate-400">{s.label}</div>
              <div className="mt-1 text-2xl font-semibold text-slate-100">{s.value}</div>
            </div>
          ))}
        </div>

        {panels && (panels.social || panels.seo || panels.leadGen) && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 mb-6 overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-700/50">
              <h2 className="text-lg font-semibold text-slate-100">
                Commentary{panelMonth ? ` — ${prettyMonth(panelMonth)}` : ''}
              </h2>
            </div>
            <div className="p-6 space-y-5">
              {PANEL_LABELS.filter((p) => panels[p.key]).map((p) => (
                <div key={p.key}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                    {p.label}
                  </div>
                  {/* Plain text. whitespace-pre-wrap preserves the author's
                      line breaks without interpreting any markup. */}
                  <p className="text-sm text-slate-200 whitespace-pre-wrap">{panels[p.key]}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4 mb-6">
          {report.months.map((m) => (
            <div
              key={m.month}
              className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden"
            >
              <div className="px-6 py-3 border-b border-slate-700/50 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-100">
                  {prettyMonth(m.month)}
                  {m.monthToDate && (
                    <span className="ml-2 text-xs font-normal uppercase tracking-wider text-amber-300">
                      month to date
                    </span>
                  )}
                </h3>
                <span className="text-sm text-slate-400">
                  {m.totalLeads} {m.totalLeads === 1 ? 'lead' : 'leads'}
                  {m.totalSpendCents != null && m.totalSpendCents > 0 && (
                    <span className="ml-2">· {aud(m.totalSpendCents)} spend</span>
                  )}
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-700/50">
                <div className="p-6">
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-3">
                    By source
                  </div>
                  <ul className="space-y-2">
                    {m.bySource.map((s) => (
                      <li
                        key={s.source}
                        className="flex items-center justify-between gap-4 text-sm text-slate-200"
                      >
                        <span>{prettySource(s.source)}</span>
                        <span className="font-mono text-slate-400">{s.leads}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-6">
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-3">
                    By campaign
                  </div>
                  {m.byCampaign.length === 0 ? (
                    <p className="text-sm text-slate-400">No campaign tags this month.</p>
                  ) : (
                    <ul className="space-y-2">
                      {m.byCampaign.map((c) => (
                        <li
                          key={`${c.source}::${c.campaign}`}
                          className="flex items-start justify-between gap-4 text-sm text-slate-200"
                        >
                          <span className="min-w-0">
                            <span className="block truncate">{c.campaign}</span>
                            <span className="text-xs text-slate-400">
                              {prettySource(c.source)}
                            </span>
                          </span>
                          <span className="font-mono text-slate-400 shrink-0">{c.leads}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              {spendIncluded !== false && m.hasSpendRows === false && (
                <div className="px-6 py-4 border-t border-slate-700/50 text-sm text-slate-400">
                  No ad spend data has arrived for this month.
                </div>
              )}
              {(m.spendByProvider?.length ?? 0) > 0 && (
                <div className="px-6 py-4 border-t border-slate-700/50">
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-3">
                    Ad spend &amp; cost per lead
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {m.spendByProvider!.map((p) => (
                      <div
                        key={p.provider}
                        className="flex items-center justify-between gap-4 text-sm text-slate-200"
                      >
                        <span>{prettySource(p.provider)}</span>
                        <span className="font-mono text-slate-400">
                          {aud(p.spendCents)}
                          {' · '}
                          {p.costPerLeadCents !== null
                            ? `${aud(p.costPerLeadCents, 2)}/lead`
                            : 'no leads'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(m.crmEnquirySource?.totalDeals ?? 0) > 0 && (
                <div className="px-6 py-4 border-t border-slate-700/50">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">
                      MyCRM enquiry source — distinct CRM deals
                    </div>
                    <span className="text-xs text-slate-400">
                      {m.crmEnquirySource!.coveragePct !== null
                        && m.crmEnquirySource!.coveragePct !== undefined
                        ? `${m.crmEnquirySource!.coveragePct}% known`
                        : 'coverage not available'}
                    </span>
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {m.crmEnquirySource!.byCategory.map((row) => (
                      <li
                        key={`${row.isUnknown ? 'unknown' : 'known'}:${row.category}`}
                        className="flex items-center justify-between gap-4 text-sm text-slate-200"
                      >
                        <span className={row.isUnknown ? 'text-amber-300' : undefined}>
                          {row.category}
                        </span>
                        <span className="font-mono text-slate-400">{row.deals}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center py-6 text-slate-500 text-sm border-t border-slate-700/50">
          <p>
            This is a shared, read-only view. Months are {report.provenance.timezone} calendar
            months.
          </p>
          <p className="mt-2">
            <a
              href="https://valutten.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 font-medium"
            >
              Powered by VALUTTEN
            </a>
            {' '}&bull;{' '}
            Commission tracking and analytics for Australian mortgage brokers
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedMarketingSourceReportPage;
